import React from 'react';
import Fader from 'react-fader';
import { Route } from 'react-router-dom';
import Switch from 'react-router-transition-switch';

import ApplicationLayout from 'components/ApplicationLayout';
import SettingsSubMenu from 'components/SettingsSubMenu';
import useIsMobile from 'hooks/useIsMobile';
import Booking from 'screens/components/Booking';
import CustomerSupportV2 from 'screens/components/CustomerSupportV2';
import DashboardReportDetails from 'screens/components/DashboardReports/components/DashboardReportDetails';
import GuestNotAuthorized from 'screens/components/Login/forms/GuestNotAuthorized';
import Onboarding from 'screens/components/Onboarding';
import Root from 'screens/components/Root';
import TravelRequested from 'screens/components/TravelRequested';
import AdminOnboarding from 'screens/containers/AdminOnboarding';
import AirlineException from 'screens/containers/AirlinesException';
import AutosList from 'screens/containers/AutosList';
import BillingCompany from 'screens/containers/BillingCompany';
import BillingCompanyList from 'screens/containers/BillingCompanyList';
import CompanyBillingContainer from 'screens/containers/CompanyBilling';
import CompanyBillingAddressEditContainer from 'screens/containers/CompanyBillingAddressEdit';
import CompanyBillingEditContainer from 'screens/containers/CompanyBillingEdit';
import CompanyBillingMapEditContainer from 'screens/containers/CompanyBillingMapEdit';
import CostCenter from 'screens/containers/CostCenter';
import CostCenterList from 'screens/containers/CostCenterList';
import CostCenterReportContainer from 'screens/containers/CostCenterReportContainer';
import Dashboard from 'screens/containers/Dashboard';
import DashboardReportsContainer from 'screens/containers/DashboardReports';
import Expense from 'screens/containers/Expense';
import ExpensesList from 'screens/containers/ExpensesList';
import ExpensesListImport from 'screens/containers/ExpensesListImport';
import FlightsList from 'screens/containers/FlightsList';
import Group from 'screens/containers/Group';
import GroupList from 'screens/containers/GroupList';
import HotelsList from 'screens/containers/HotelsList';
import InviteContainer from 'screens/containers/Invite';
import Login from 'screens/containers/Login';
import LoginEmail from 'screens/containers/LoginEmail';
import LoginSso from 'screens/containers/LoginSso';
import ForgotPassword from 'screens/containers/ForgotPassword';
import OAuthDefaultContainer from 'screens/containers/OAuthDefaultContainer';
import PaymentAddMethod from 'screens/containers/PaymentAddMethod';
import PaymentCardSelection from 'screens/containers/PaymentCardSelection';
import PaymentGroupCardSelection from 'screens/containers/PaymentGroupCardSelection';
import PaymentTravelStatus from 'screens/containers/PaymentTravelStatus';
import RegistrationOnboardingContainer from 'screens/containers/RegistrationOnboarding';
import RequestHistory from 'screens/containers/RequestHistory';
import ResellerCompany from 'screens/components/ResellerCompany'; //reseller
import Rule from 'screens/containers/Rule';
import GroupSelectRuleContainer from 'screens/containers/RuleSelection';
import RulesListContainer from 'screens/containers/RulesList';
import TravelerProfile from 'screens/containers/TravelerProfile';
import TravelerProfileEdit from 'screens/containers/TravelerProfileEdit';
import TravelerTripPreferences from 'screens/containers/TravelerTripPreferences';
import TravelReportForm from 'screens/containers/TravelReportFormContainer';
import TravelsWithExpensesList from 'screens/containers/TravelsWithExpensesList';
import UserCountrySelectionContainer from 'screens/containers/UserCountrySelection';
import UsersListContainer from 'screens/containers/Users';
import UsersForm from 'screens/containers/UsersForm';
import Welcome from 'screens/containers/Welcome';
import ResellerCustomization from 'screens/components/ResellerCustomization';
import CompanyConfigurationUpdate from 'screens/components/CompanyConfigurationUpdate';
import AdminToolContainer from 'screens/components/AdminTool/AdminTool';
import AdminToolUpdateUser from 'screens/components/AdminToolUpdateUser';
import AdminToolAddExtraCharge from 'screens/components/AdminToolAddExtraCharge';
import AllReservationsReportContainer from 'screens/containers/AllReservationsReportContainer';
import AdminToolAddImplementator from 'screens/components/AdminToolAddImplementator/Index';
import AdminToolUpdateItinerary from 'screens/components/AdminToolUpdateItinerary/Index';
import TravelReasonContainer from 'screens/containers/TravelReason';
import ResellerFormContainer from 'screens/containers/ResellerFormContainer';
import RejectedReasonContainer from 'screens/containers/RejectedReason';
import RejectedFormContainer from 'screens/containers/RejectedReasonFormContainer';
import BreakRulesReasonContainer from 'screens/containers/BreakRulesReason';
import BreakRulesFormContainer from 'screens/containers/BreakRulesFormContainer';
import CreateResellerForm from 'screens/components/CreatedReseller';
import { OauthNsLogin } from './publicRouter';
import CreateResellerContainer from 'screens/containers/CreateReseller';

import AdminToolSendTravelToApprove from 'screens/components/AdminToolSendTravelToApprove';
import AdminToolRetryApproval from 'screens/components/AdminToolRetryApproval';

const SuperAdminRouter = () => {
  const isMobile = useIsMobile();

  return (
    <Switch
      render={({ children }) => (
        <ApplicationLayout>
          <Fader viewStyle={isMobile ? { height: '100vh' } : {}}>{children}</Fader>
        </ApplicationLayout>
      )}
    >
      <Route path="/corporate/invite" component={InviteContainer} />
      <Route path="/corporate/registration" component={RegistrationOnboardingContainer} />
      <Route exact path="/corporate/adminonboarding" component={AdminOnboarding} />
      <Route exact path="/corporate/login-email" component={LoginEmail} />
      <Route exact path="/corporate/login/sso" component={LoginSso} />
      <Route exact path="/corporate/login/forgotpassword" component={ForgotPassword} />
      <Route path="/corporate/login" component={Login} />
      <Route path="/corporate/customerSupport" component={CustomerSupportV2} />
      <Route exact path="/corporate/dashboard" component={Dashboard} />
      <Route exact path="/corporate/dashboard/v2" component={Dashboard} />
      <Route exact path="/corporate/dashboardReports" component={DashboardReportsContainer} />
      <Route exact path="/corporate/dashboardReports/reportDetail" component={DashboardReportDetails} />
      <Route exact path="/corporate/rules" component={RulesListContainer} />
      <Route exact path="/corporate/rule" component={Rule} />
      <Route exact path="/corporate/rule/:ruleId" component={Rule} />
      <Route exact path="/corporate/payments/travel/:travelId" component={PaymentTravelStatus} />
      <Route exact path="/corporate/paymentMethods" component={PaymentCardSelection} />
      <Route exact path="/corporate/paymentMethods/add" component={PaymentAddMethod} />
      <Route exact path="/corporate/group/:groupId/payment" component={PaymentGroupCardSelection} />
      <Route exact path="/corporate/group/:groupId/payment/add" component={PaymentAddMethod} />
      <Route exact path="/corporate/group/:groupId" component={Group} />
      <Route exact path="/corporate/group" component={Group} />
      <Route exact path="/corporate/groupList" component={GroupList} />
      <Route exact path="/corporate/costCenterList" component={CostCenterList} />
      <Route exact path="/corporate/costCenter/:costCenterId" component={CostCenter} />
      <Route exact path="/corporate/costCenter/" component={CostCenter} />
      <Route exact path="/corporate/usersList" component={UsersListContainer} />
      <Route exact path="/corporate/usersForm" component={UsersForm} />
      <Route exact path="/corporate/usersForm/:userId" component={UsersForm} />
      <Route exact path="/corporate/travelReport" component={TravelReportForm} />
      <Route path="/corporate/travelRequested" component={TravelRequested} />
      <Route path="/corporate/booking" component={Booking} />
      <Route path="/corporate/flights" component={FlightsList} />
      <Route path="/corporate/hotels" component={HotelsList} />
      <Route path="/corporate/autos" component={AutosList} />
      <Route exact path="/corporate/requestHistory" component={RequestHistory} />
      <Route exact path="/corporate/airlineException" component={AirlineException} />
      <Route exact path="/corporate/travelsWithExpenses" component={TravelsWithExpensesList} />
      <Route exact path="/corporate/expense" component={Expense} />
      <Route exact path="/corporate/expense/:expenseId" component={Expense} />
      <Route exact path="/corporate/expenses/:travelId" component={ExpensesList} />
      <Route exact path="/corporate/expensesImport/:travelId" component={ExpensesListImport} />
      <Route exact path="/corporate/travelerProfile" component={TravelerProfile} />
      <Route exact path="/corporate/travelerProfileEdit" component={TravelerProfileEdit} />
      <Route exact path="/corporate/travelerProfile/preferences/trip" component={TravelerTripPreferences} />
      <Route exact path="/corporate/ruleList" component={GroupSelectRuleContainer} />
      <Route exact path="/corporate/onboarding" component={Onboarding} />
      <Route exact path="/corporate/welcome" component={Welcome} />
      <Route exact path="/corporate/companyBilling" component={CompanyBillingContainer} />
      <Route exact path="/corporate/companyBillingEdit" component={CompanyBillingEditContainer} />
      <Route exact path="/corporate/companyBillingAddressEdit" component={CompanyBillingAddressEditContainer} />
      <Route exact path="/corporate/companyBillingMapEdit" component={CompanyBillingMapEditContainer} />
      <Route exact path="/corporate/settings" component={SettingsSubMenu} />
      <Route exact path="/corporate/oauth-login" component={OAuthDefaultContainer} />
      <Route exact path="/corporate/oauth-registration" component={OAuthDefaultContainer} />
      <Route exact path="/corporate/oauth-invite" component={OAuthDefaultContainer} />
      <Route exact path="/corporate/costCenterReport" component={CostCenterReportContainer} />
      <Route exact path="/corporate/oauth-login" component={OAuthDefaultContainer} />
      <Route exact path="/corporate/oauth-registration" component={OAuthDefaultContainer} />
      <Route exact path="/corporate/oauth-invite" component={OAuthDefaultContainer} />
      <Route exact path="/corporate/login-not-authorized" component={GuestNotAuthorized} />
      <Route exact path="/corporate/user-country-selection" component={UserCountrySelectionContainer} />
      <Route exact path="/corporate/billingCompanyList" component={BillingCompanyList} />
      <Route exact path="/corporate/configurations/" component={ResellerCompany} />
      <Route exact path="/corporate/billingCompany/:billingCompanyId" component={BillingCompany} />
      <Route exact path="/corporate/billingCompany/" component={BillingCompany} />
      <Route exact path="/corporate" component={Root} />
      <Route exact path="/corporate/" component={Root} />
      <Route path="/corporate/resellerCustomization" component={ResellerCustomization} />
      <Route exact path="/corporate/TravelReasons" component={TravelReasonContainer} />
      <Route exact path="/corporate/resellerForm" component={ResellerFormContainer} />
      <Route exact path="/corporate/resellerForm/:travelReasonId" component={ResellerFormContainer} />
      <Route exact path="/corporate/RejectedReasons" component={RejectedReasonContainer} />
      <Route exact path="/corporate/rejectedForm" component={RejectedFormContainer} />
      <Route exact path="/corporate/rejectedForm/:rejectedReasonId" component={RejectedFormContainer} />
      <Route exact path="/corporate/BreakRulesReasons" component={BreakRulesReasonContainer} />
      <Route exact path="/corporate/breakRulesForm" component={BreakRulesFormContainer} />
      <Route exact path="/corporate/breakRulesForm/:breakRulesId" component={BreakRulesFormContainer} />
      <Route path="/corporate/CompanyConfigurationUpdate" component={CompanyConfigurationUpdate} />
      <Route exact path="/corporate/adminTool" component={AdminToolContainer} />
      <Route exact path="/corporate/adminTool/" component={AdminToolContainer} />
      <Route exact path="/corporate/adminTool/updateUserIsActive" component={AdminToolUpdateUser} />
      <Route exact path="/corporate/adminTool/addExtraCharge" component={AdminToolAddExtraCharge} />
      <Route exact path="/corporate/adminTool/addImplementator" component={AdminToolAddImplementator} />
      <Route exact path="/corporate/adminTool/updateItinerary" component={AdminToolUpdateItinerary} />
      
      <Route exact path="/corporate/adminTool/sendTravelToApprove" component={AdminToolSendTravelToApprove} />
      <Route exact path="/corporate/adminTool/retryItineraryApproval" component={AdminToolRetryApproval} />
      <Route exact path="/corporate/allReservationsReport" component={AllReservationsReportContainer} />
      <Route exact path="/corporate/createReseller" component={CreateResellerContainer} />
      <Route exact path="/corporate/createResellerForm" component={CreateResellerForm} />
      <Route exact path="/corporate/auth/:token" component={OauthNsLogin} />
    </Switch>
  );
};

export default SuperAdminRouter;
